// src/pages/Integrations.jsx
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { queryClient } from '@/lib/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { IntegrationCard } from '@/components/features/integrations/IntegrationCard';
import { PixelSelectionModal } from '@/components/features/integrations/PixelSelectionModal';
import { ActiveCampaignSetupForm } from '@/components/features/integrations/ActiveCampaignSetupForm';

const AVAILABLE_INTEGRATIONS = [
  {
    id: 'activeCampaign',
    name: 'Active Campaign',
    description:
      'Connect your Active Campaign account to track leads and their marketing sources.',
    logo: '/img/crm/active-campaign-logo.svg',
    isAvailable: true,
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Sync your marketing source data with HubSpot CRM.',
    logo: '/img/crm/hubspot-logo.svg',
    isAvailable: false,
    comingSoon: true,
  },
  {
    id: 'keap',
    name: 'Keap',
    description: 'Integrate with Keap CRM to track your customer sources.',
    logo: '/img/crm/keap-logo.svg',
    isAvailable: false,
    comingSoon: true,
  },
];

export function IntegrationsPage() {
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showPixelModal, setShowPixelModal] = useState(false);
  const [showSetupForm, setShowSetupForm] = useState(false);
  const [selectedPixelId, setSelectedPixelId] = useState(null);

  // Get pixel ID from location state (if coming from pixel card)
  const preSelectedPixelId = location.state?.pixelId;

  useEffect(() => {
    if (preSelectedPixelId) {
      setSelectedPixelId(preSelectedPixelId);
    }
  }, [preSelectedPixelId]);

  const handleIntegrationSetup = (integration) => {
    if (!integration.isAvailable) return;

    setSelectedIntegration(integration);

    // If we have a pre-selected pixel, show setup form directly
    if (preSelectedPixelId) {
      setShowSetupForm(true);
      return;
    }

    // If user has pixels, show selection modal
    if (profile?.connectedPixels?.length > 0) {
      setShowPixelModal(true);
      return;
    }

    // If no pixels, redirect to pixel creation
    navigate('/pixels');
  };

  const handlePixelSelection = (pixelId) => {
    setSelectedPixelId(pixelId);
    setShowPixelModal(false);
    setShowSetupForm(true);
  };

  const handleIntegrationComplete = () => {
    setShowSetupForm(false);
    setSelectedPixelId(null);
    // Refresh the integration status
    queryClient.invalidateQueries(['pixels']);
  };

  return (
    <div className="sl-integrations-page space-y-6">
      <div className="sl-integrations-header">
        <h1 className="sl-page-title text-2xl font-bold text-gray-900">
          CRM Integrations
        </h1>
        <p className="sl-page-description mt-2 text-gray-600">
          {selectedPixelId ? (
            <>
              Set up integrations for pixel{' '}
              <code className="text-purple-600 bg-purple-50 px-2 py-1 rounded">
                {selectedPixelId}
              </code>
            </>
          ) : (
            'Connect your pixels with your favorite CRM platforms to track marketing sources.'
          )}
        </p>
      </div>

      <div className="sl-integrations-grid grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {AVAILABLE_INTEGRATIONS.map((integration) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            onSetup={handleIntegrationSetup}
            connectedPixels={profile?.connectedPixels || []}
          />
        ))}
      </div>

      {/* Pixel Selection Modal */}
      {!preSelectedPixelId && (
        <PixelSelectionModal
          open={showPixelModal}
          onOpenChange={setShowPixelModal}
          onSelect={handlePixelSelection}
          pixels={profile?.connectedPixels || []}
        />
      )}

      {/* Integration Setup Form */}
      {selectedIntegration?.id === 'activeCampaign' && (
        <ActiveCampaignSetupForm
          pixelId={selectedPixelId}
          open={showSetupForm}
          onOpenChange={setShowSetupForm}
          onComplete={handleIntegrationComplete}
        />
      )}
    </div>
  );
}
